// Service for fetching NIFC / WFIGS wildland fire data.
// The React app calls the Herding Helpers API proxy at /herding_api/nifc
// because some NIFC endpoints do not send CORS headers and polygon payloads
// can be large.

const NIFC_PROXY_URL = '/herding_api/nifc';

// Cache by bounding box + layer. WFIGS perimeters update periodically,
// so a short TTL keeps the map fresh without hammering the API.
const cache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCache(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

async function readJsonOrText(response) {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[NIFC] non-JSON response body:', text);
    const snippet = text.slice(0, 200).replace(/\s+/g, ' ');
    throw new Error(`Expected JSON but got: ${snippet}`);
  }
}

/**
 * Fetch NIFC WFIGS fire perimeters or incident locations for a bounding box.
 *
 * @param {Object} options
 * @param {number} options.west   - Western boundary (longitude)
 * @param {number} options.south  - Southern boundary (latitude)
 * @param {number} options.east   - Eastern boundary (longitude)
 * @param {number} options.north  - Northern boundary (latitude)
 * @param {string} [options.layer='perimeters'] - 'perimeters' or 'incidents'
 * @returns {Promise<GeoJSON FeatureCollection>}
 */
export async function fetchNIFCFiresGeoJSON({
  west,
  south,
  east,
  north,
  layer = 'perimeters',
} = {}) {
  const params = new URLSearchParams({
    west: String(west),
    south: String(south),
    east: String(east),
    north: String(north),
    layer,
  });
  const url = `${NIFC_PROXY_URL}?${params.toString()}`;
  const cacheKey = url;

  const cached = getCache(cacheKey);
  if (cached) return cached;

  // eslint-disable-next-line no-console
  console.log('[NIFC] fetching:', url);

  const response = await fetch(url, {
    headers: { Accept: 'application/json, application/geo+json' },
  });

  const payload = await readJsonOrText(response);

  if (!response.ok) {
    // eslint-disable-next-line no-console
    console.warn('[NIFC] proxy error payload:', payload);
    const message = payload?.error || JSON.stringify(payload);
    throw new Error(`NIFC proxy HTTP ${response.status}: ${message}`);
  }

  if (!payload || payload.type !== 'FeatureCollection') {
    throw new Error('NIFC response was not a GeoJSON FeatureCollection');
  }

  // eslint-disable-next-line no-console
  console.log('[NIFC] features received:', payload.features?.length ?? 0);

  setCache(cacheKey, payload);
  return payload;
}

/**
 * Build a bounding box around a center point with a given degree padding.
 */
export function bboxAround(center, padding = 5.0) {
  return {
    west: center.lng - padding,
    south: center.lat - padding,
    east: center.lng + padding,
    north: center.lat + padding,
  };
}

/**
 * Clear the in-memory NIFC cache.
 */
export function clearNIFCCache() {
  cache.clear();
}

const nifcService = {
  fetchNIFCFiresGeoJSON,
  bboxAround,
  clearNIFCCache,
};

export default nifcService;
